import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicsByCategory, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [comics, setComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];

  useEffect(() => {
    let result = getComicsByCategory(slug || 'all');
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    setComics(result);
    setCurrentPage(1);
  }, [slug, sortBy]);

  const totalPages = Math.ceil(comics.length / perPage);
  const paginatedComics = comics.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-1.5 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to="/category/all" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-800 font-medium">{currentCategory.name}</li>
          </ol>
        </nav>

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {slug === 'all' ? '全部漫画' : currentCategory.name + '漫画'}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 <strong>{comics.length}</strong> 部作品
              </p>
            </div>
            <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
              {([
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ] as const).map(s => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {comics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画资源</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {paginatedComics.map(comic => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 韩漫在线免费阅读`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          comic.status === '连载中'
                            ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                            : 'bg-accent-500 text-background-50 dark:text-foreground-950'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-background-50/90 text-xs line-clamp-2">{comic.description}</p>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-star-fill text-yellow-400 text-[10px]"></i>
                      <span>{comic.rating}</span>
                      <span className="mx-1">·</span>
                      <span>{comic.chapters}话</span>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 mt-10">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                        p === currentPage
                          ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}